import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faUnlock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import { SmartyStreetsService } from '../../../core/common/smart-streets.service';
import { SecurityService } from '../../../core/security/security.service';
import { SystemMessage, SystemMessageService } from '../../../core/system-message/system-message-service';
import { EditUserVm } from '../../../shared/generated/Administration/Models/User/EditUser/EditUserVm';
import { UserChangePasswordModalComponent } from '../user-change-password-modal/user-change-password-modal.component';
import { UserPermissionsModalComponent } from '../user-permissions-modal/user-permissions-modal.component';
import { UserJsVm } from '../../../shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { GlobalVariablesService } from '../../../services/global-variables.service';
import { LoginTypeEnum } from '../../../core/enums/generated/LoginTypeEnum';

@Component({
    selector: 'pcg-user-security',
    templateUrl: './user-security.component.html',
    styleUrls: ['./user-security.component.scss'],
    standalone: false
})
export class UserSecurityComponent implements OnInit, OnDestroy {

	userNameValidation = { pattern: "Only letters, numbers, and symbols ( . @ - _ ' ) are allowed" };
	
	formGroup = EditUserVm.Form;
	model = EditUserVm.GetModel(this.formGroup);
	user: UserJsVm;

	// Security variables
	isLockedOut = false;
	canManageUsers = false;
	canSeePasswordButtons = false;
	isCurrentUser = false;
	iAmMe: boolean;
	newUserParam = false;
	isSSO = false;

	id: number;
	
	timeout: any
	
	// Fa icons
	faIconName = { faUnlock, faInfoCircle };	

	constructor(
		private route: ActivatedRoute
		, private httpClient: HttpClient
		, public router: Router
		, private modalService: NgbModal
		, private ms: SystemMessageService
		, public smartyStreetsService: SmartyStreetsService
		, private sec: SecurityService
		, private changeDetectorRef: ChangeDetectorRef
		, private gvs: GlobalVariablesService
	) { }

	ngOnInit() {
		this.user = this.sec.getUser();
		this.addSecuritySubscription();
		this.addRouteSubscription();
	}

	/** Update the local dynamic security object when the security updates */
	addSecuritySubscription() {
		this.sec.security$.subscribe(() => {
			this.canManageUsers = this.sec.hasAccess(o => o.manageSiteUsers);
            if (!this.canManageUsers) { this.formGroup?.controls?.userName?.disable(); }
            else { this.formGroup?.controls?.userName?.enable(); }
			this.sec.setFormAccess(this.formGroup, sec => sec.manageSiteUsers);            
		});
	}

	/** Subscribe to route to pick up id changes and update page accordingly */
	addRouteSubscription() {		
		this.route.paramMap.subscribe(map => {
			// Allow user to view page if it is either their profile
			// or they have admin access to view other people's profiles
			const user = this.sec.getUser();
			this.id = parseInt(map.get('id')!, 10);
			let api = this.newUserParam === true 
				? `api/Administration/Users/GetUser/${this.id}`
				: `api/Administration/Users/GetUser/${this.id}?isNewUser=${true}`;
			if (
				!this.canManageUsers 
				&& this.id !== user?.id
			) { this.router.navigateByUrl('/'); }
			this.isCurrentUser = this.id === this.sec.getUser()?.id;			
			this.httpClient.get(api)
				.subscribe((vm: any) => {
					this.formGroup.patchValue(vm);
					this.canSeePasswordButtons = vm.isActive 
						&& !vm.isLockedOut 
						&& vm.userId !== this.sec.getUser().id 
						&& vm.userId !== 0;
					this.isLockedOut = vm.isLockedOut;
					
					if (
						vm.orgLoginType === LoginTypeEnum.SSO
						|| vm.orgLoginType == LoginTypeEnum.Both
					) { this.isSSO = true; } 
					else { this.isSSO = false; }

					this.gvs.userName.set(vm?.fullName!);
				}
			);
		});
	}

	/** Called on click of the reset password and welcome email buttons */
	resetPassword(
		isWelcomeEmail: boolean
		, isSystemMessage: boolean
	) {		
		this.httpClient.post('api/Account/ForgotPassword', {
			email: this.formGroup.value.email
			, isWelcomeEmail
		}).subscribe((msg: any) => {
			if (msg.isSuccessful) {
				this.ms.setSystemMessage(`An email has been sent to the user with a ${isWelcomeEmail ? 'welcome' : 'reset password'} link.`);
			} else { this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error'); }
		});
		// timed out so that the user can see the confirmation system message before being redirected
		if (isSystemMessage) { setTimeout(() => { this.router.navigateByUrl("users/list") }, 500); }	
	}

	/** Opens the change password modal.
	 * Called on click of the Change Password button when user looking at their profile. */
	openChangePasswordModal() { this.modalService.open(UserChangePasswordModalComponent); }
	openPermissionsInfoModal() { this.modalService.open(UserPermissionsModalComponent, { size: 'xl' }); }

	// saves permission whenever it is changed
	handlePermissionEvents() {
		if (!this.iAmMe) {
			this.ms.post('api/Administration/Users/SaveUserPermission', this.formGroup)
				.subscribe((response: SystemMessage) => { this.saveUser(response); });
		}		
	}

	// saves username when the username input loses focus
	handleUsernameEvents() {
		this.ms.post('api/Administration/Users/SaveUsername', this.formGroup)
			.subscribe((response: SystemMessage) => { this.saveUser(response); });
	}

	saveUser(msg: SystemMessage, tabName = null) {
		if (msg.value) {
			const model = this.formGroup.value;			
			this.id = model.userId;
			this.canSeePasswordButtons = model.isActive 
				&& !model.isLockedOut 
				&& model.userId !== this.sec.getUser().id 
				&& model.userId !== 0;
			this.sec.setSecurity(undefined!, msg.value.userJsVm, msg.value.dynamicSecurity);
		}
		if (!msg.isSuccessful) { setTimeout(() => { this.changeDetectorRef.detectChanges(); }, 1); } 
	}

	ngOnDestroy() { this.formGroup = null!; }
}